import { DASHBOARD_PATH } from "../../../utils/constants/routes";

// Dashboard Tour — 8 steps walking through the analytics dashboard.
// Tour key: "dashboard"
//
// Resume is fully supported — each step's before() ensures the correct
// page and tab state independently so the tour can start from any step.

export function getDashboardTourSteps(navigate) {
  // Navigates to /user/dashboard if not already there.
  const goToDashboard = async (delay = 800) => {
    if (window.location.pathname === DASHBOARD_PATH) return;
    navigate(DASHBOARD_PATH);
    await new Promise((resolve) => setTimeout(resolve, delay));
  };

  // Ensures the given tab (overview | campaigns) is active.
  // Checks whether the active indicator class is present on the tab wrapper
  // to avoid a redundant click when the tab is already selected.
  const ensureTab = async (tabId) => {
    const tabEl = document.querySelector(`[data-tour="dashboard-tab-${tabId}"]`);
    if (!tabEl) return;
    // [class*="tabIndicatorActive"] matches the CSS-module-hashed active class
    if (tabEl.querySelector('[class*="tabIndicatorActive"]')) return;
    const btn = tabEl.querySelector("button") || tabEl;
    btn.click();
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  // Polls for a CSS selector every 1 s for up to 10 s.
  // Resolves as soon as the element is found (or after the timeout).
  // On normal flow the element is already present and resolves immediately.
  const waitForElement = (selector, timeout = 10000, interval = 1000) => {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) { resolve(); return; }
      const start = Date.now();
      const timer = setInterval(() => {
        if (document.querySelector(selector) || Date.now() - start >= timeout) {
          clearInterval(timer);
          resolve();
        }
      }, interval);
    });
  };

  return [
    // ─── Step 1: Dashboard sidebar nav item ──────────────────────────────────
    {
      target: '[data-tour="dashboard-nav"]',
      placement: "right",
      title: "Your Analytics Hub",
      content:
        "Your command center for performance insights. Every time you want to know how your outreach is doing, this is the place to be.",
      blockTargetInteraction: true,
      hideBackButton: true,
      spotlightPadding: 4,
      spotlightRadius: 8,
    },

    // ─── Step 2: Overview / Campaigns tab bar ────────────────────────────────
    // Highlights both tab buttons (inner wrapper, not the full-width container).
    // Prereq: on /user/dashboard (any tab — no tab switch needed here).
    {
      target: '[data-tour="dashboard-tabs"]',
      placement: "bottom",
      title: "Your Two Dashboard Views",
      content:
        "We give you two views: Overview for the big picture, Campaigns for campaign-level deeper analytics. Let's cover the highlights. (Numbers shown are dummy data.)",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await goToDashboard();
      },
    },

    // ─── Step 3: Primary KPI section (hero cards + stat strip) ───────────────
    // Prereq: /user/dashboard + Overview tab active.
    // tooltipMaxWidth broadens the tooltip so it doesn't clip above the cards.
    {
      target: '[data-tour="dashboard-kpi-section"]',
      placement: "top",
      title: "Your KPI Cards",
      content:
        "We start with your KPI cards. They show emails sent, qualified leads, active campaigns, and much more at a glance.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      tooltipMaxWidth: 540,
      before: async () => {
        await goToDashboard();
        await ensureTab("overview");
        await waitForElement('[data-tour="dashboard-kpi-section"]');
      },
    },

    // ─── Step 4: Pipeline Funnel card ────────────────────────────────────────
    // Prereq: /user/dashboard + Overview tab active.
    {
      target: '[data-tour="dashboard-pipeline-funnel"]',
      placement: "left",
      title: "The Pipeline Funnel",
      content:
        "This funnel shows prospects moving from first contacted to a booked meeting, so you can spot where they drop off.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await goToDashboard();
        await ensureTab("overview");
        await waitForElement('[data-tour="dashboard-pipeline-funnel"]');
      },
    },

    // ─── Step 5: Top Campaigns card ──────────────────────────────────────────
    // Prereq: /user/dashboard + Overview tab active.
    {
      target: '[data-tour="dashboard-top-campaigns"]',
      placement: "left",
      title: "Your Top Campaigns",
      content:
        "This card shows your campaigns ranked by meetings booked, so you can see which ones are performing the best.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await goToDashboard();
        await ensureTab("overview");
        await waitForElement('[data-tour="dashboard-top-campaigns"]');
      },
    },

    // ─── Step 6: Top Engaged Contacts table ─────────────────────────────────
    // Prereq: /user/dashboard + Overview tab active.
    {
      target: '[data-tour="dashboard-top-contacts-table"]',
      placement: "top",
      title: "Top Engaged Contacts",
      content:
        "Contacts who have recently engaged show up here. If one has a 'Hot' signal, that's your cue to reach out.",
      blockTargetInteraction: true,
      spotlightPadding: 6,
      spotlightRadius: 6,
      tooltipMaxWidth: 540,
      before: async () => {
        await goToDashboard();
        await ensureTab("overview");
        await waitForElement('[data-tour="dashboard-top-contacts-table"]');
      },
    },

    // ─── Step 7: Tab bar again — Campaigns tab now active ───────────────────
    // Highlights the same tab buttons wrapper with Campaigns tab now active.
    // Prereq: /user/dashboard + Campaigns tab active.
    {
      target: '[data-tour="dashboard-tabs"]',
      placement: "bottom",
      title: "The Campaigns Tab",
      content:
        "That was a glimpse of what's in Overview. Let's take a very quick look at the Campaigns tab too!",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await goToDashboard();
        await ensureTab("campaigns");
        await waitForElement('[data-tour="dashboard-tabs"]');
      },
    },

    // ─── Step 8: Campaigns tab — centered overview tooltip ───────────────────
    // Prereq: /user/dashboard + Campaigns tab active.
    {
      target: "body",
      placement: "center",
      title: "Campaign Analytics, All in One Place",
      content:
        "Here you can track campaign-level stats, reply rates, reply quality, and more. Now you're all set to explore!",
      blockTargetInteraction: false,
      spotlightPadding: 0,
      before: async () => {
        await goToDashboard();
        await ensureTab("campaigns");
      },
    },
  ];
}
